import React from 'react';
import FormCheckbox from './FormCheckbox';
import { classNames } from '../utils/classNames';

// A list of checkboxes in groups: each group has one checkbox for all its items ("Rome · 18"), mixed when only some of its
// items are on, and its items under it, in one or two columns. The caller owns the selection: onChange receives the ids
// of all items that are on after the click, in the order of the groups. The Component knows nothing about the items: the
// caller gives the groups, their order and the order of the items.

export type ChecklistItem = { id: string, label: string };
export type ChecklistGroup = { id: string, label: string, items: ChecklistItem[] };

type Props = {
	groups: ChecklistGroup[],
	// The ids of the items that are on.
	selected: string[],
	onChange: (selected: string[]) => void,
	// 2 (default): two columns of items from the sm width; 1: one column.
	columns?: 1 | 2,
	disabled?: boolean,
};

const GroupedChecklist: React.FunctionComponent<Props> = ({ groups, selected, onChange, columns = 2, disabled = false }) => {
	const on = new Set(selected);
	const set = (ids: string[], value: boolean) => {
		const next = new Set(on);
		ids.forEach(id => value ? next.add(id) : next.delete(id));
		onChange(groups.flatMap(group => group.items.map(item => item.id)).filter(id => next.has(id)));
	};
	return <div className="flex flex-col gap-4">
		{groups.map(group => {
			const ids = group.items.map(item => item.id);
			const count = ids.filter(id => on.has(id)).length;
			return <fieldset key={group.id} className="flex min-w-0 flex-col gap-2">
				<legend className="sr-only">{group.label}</legend>
				<FormCheckbox name={`group-${group.id}`} label={`${group.label} · ${ids.length}`} labelExtraClassName="font-semibold text-gray-900"
					checked={ids.length > 0 && count === ids.length} indeterminate={count > 0 && count < ids.length} disabled={disabled}
					onChange={event => set(ids, event.target.checked)} />
				<div className={classNames('grid grid-cols-1 gap-x-6 gap-y-2 pl-7', columns === 2 ? 'sm:grid-cols-2' : '')}>
					{group.items.map(item => <FormCheckbox key={item.id} name={`item-${item.id}`} label={item.label}
						checked={on.has(item.id)} disabled={disabled} onChange={event => set([item.id], event.target.checked)} />)}
				</div>
			</fieldset>;
		})}
	</div>;
};

export default GroupedChecklist;
